import { View, Text, StyleSheet, ScrollView, Pressable, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { ArrowLeft } from 'lucide-react-native';
import { theme } from '@/constants/theme';

const LAST_UPDATED = 'נובמבר 2024';

export default function DisclaimerScreen() {
  const router = useRouter();

  return (
    <View style={styles.container}>
      {/* SEO/GEO FIX: add page-level canonical metadata for legal pages included in sitemap. */}
      {Platform.OS === 'web' && (
        <Head>
          <title>הגבלת אחריות | Kursi</title>
          <meta name="description" content="הבהרות משפטיות והגבלת אחריות של Kursi לגבי מידע קורסים, מחירים ותוצאות לימוד." />
          <meta name="robots" content="index,follow" />
          <link rel="canonical" href="https://kursiaii.com/legal/disclaimer" />
          <meta property="og:title" content="הגבלת אחריות | Kursi" />
          <meta property="og:description" content="הבהרות משפטיות והגבלת אחריות של Kursi לגבי מידע קורסים, מחירים ותוצאות לימוד." />
          <meta property="og:url" content="https://kursiaii.com/legal/disclaimer" />
        </Head>
      )}

      {/* HEADER */}
      <View style={styles.header} accessibilityRole="header">
        <Pressable onPress={() => router.back()} style={styles.backButton}>
          <ArrowLeft color={theme.colors.text} size={24} />
        </Pressable>
        <Text style={styles.headerTitle}>הגבלת אחריות</Text>
        <View style={styles.placeholder} />
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <Text style={styles.title}>הגבלת אחריות - Kursi</Text>
        <Text style={styles.updateDate}>עודכן לאחרונה: {LAST_UPDATED}</Text>

  

        {/* 1 */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>1. מה זה Kursi</Text>
          <Text style={styles.paragraph}>
            Kursi היא פלטפורמה לחיבור בין משתמשים למנטורים, קורסים, תוכניות ליווי והכשרות –
            באמצעות מערכת המלצות מבוססת בינה מלאכותית.
          </Text>
          <Text style={styles.paragraph}>
            <Text style={styles.bold}>
              Kursi אינה מוסד לימודי, אינה גוף מפקח ואינה צד לעסקאות.
            </Text>
          </Text>
        </View>

        {/* 2 */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>2. מערכת AI והמלצות אוטומטיות</Text>
          <Text style={styles.listItem}>• ההמלצות נוצרות אוטומטית ע"י מערכת AI</Text>
          <Text style={styles.listItem}>• ייתכנו טעויות, אי־דיוקים או חוסרי התאמה</Text>
          <Text style={styles.listItem}>• אין לראות בהמלצות ייעוץ מקצועי מכל סוג</Text>
          <Text style={styles.paragraph}>
            <Text style={styles.bold}>
              אין לראות בהמלצות משום ייעוץ פיננסי, השקעות, קריירה, משפטי או לימודי.
              כל החלטה שאתה מקבל – היא באחריותך בלבד.
            </Text>
          </Text>
        </View>

        {/* 3 */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>3. מנטורים, קורסים וצדדים שלישיים</Text>
          <Text style={styles.listItem}>• כל מנטור או קורס הוא ספק חיצוני עצמאי</Text>
          <Text style={styles.listItem}>• Kursi אינה אחראית על התוכן, האיכות או המחיר</Text>
          <Text style={styles.listItem}>• כל התקשרות היא ישירות בינך לבין הספק</Text>
          <Text style={styles.paragraph}>
            <Text style={styles.bold}>
              כל עסקה, תשלום, התחייבות או סכסוך – מתבצעים בינך לבין המנטור בלבד.
              Kursi אינה צד לעסקה.
            </Text>
          </Text>
        </View>

        {/* 4 */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>4. לידים, התאמות ופניות</Text>
          <Text style={styles.listItem}>• חיפושים ובקשות משמשים לזיהוי צרכים</Text>
          <Text style={styles.listItem}>• פרטי פנייה עשויים לעבור למנטורים רלוונטיים</Text>
          <Text style={styles.paragraph}>
            <Text style={styles.bold}>
              שימוש בפלטפורמה מהווה הסכמה להעברת פרטים לצורך יצירת קשר מקצועי בלבד.
            </Text>
          </Text>
        </View>

        {/* 5 */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>5. אבטחת מידע</Text>
          <Text style={styles.listItem}>• שום מערכת אינה חסינה ב־100%</Text>
          <Text style={styles.listItem}>• עלולות להתרחש חדירות או תקלות</Text>
          <Text style={styles.paragraph}>
            <Text style={styles.bold}>
              מסירת מידע אישי היא באחריותך בלבד.
            </Text>
          </Text>
        </View>

        {/* 6 */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>6. זמינות השירות</Text>
          <Text style={styles.listItem}>• השירות עשוי להיות לא זמין</Text>
          <Text style={styles.listItem}>• תכונות עשויות להשתנות או להיעלם</Text>
          <Text style={styles.listItem}>• אין התחייבות לרציפות פעולה</Text>
        </View>

        {/* 7 */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>7. הגבלת אחריות</Text>
          <Text style={styles.listItem}>• אין אחריות לנזקים כספיים או תוצאתיים</Text>
          <Text style={styles.listItem}>• אין אחריות לאובדן הכנסות או רווחים</Text>
          <Text style={styles.listItem}>• אין אחריות להחלטות שגויות</Text>
        </View>

        {/* 8 */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>8. אחריות המשתמש</Text>
          <Text style={styles.listItem}>• בדיקת התאמה של כל קורס או מנטור</Text>
          <Text style={styles.listItem}>• בדיקת מחירים ותנאים</Text>
          <Text style={styles.listItem}>• שימוש אחראי במידע</Text>
        </View>

        {/* 9 */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>9. דין חל</Text>
          <Text style={styles.paragraph}>
            מסמך זה כפוף אך ורק לדיני מדינת ישראל.
          </Text>
        </View>

        {/* FOOTER */}
        <View style={styles.footer}>
          <Text style={styles.footerTitle}>אישור אחרון</Text>
          <Text style={styles.footerText}>
            בעצם השימוש בפלטפורמה Kursi אתה מאשר שקראת, הבנת והסכמת לתנאים.
          </Text>
          <Text style={styles.footerHighlight}>
            השימוש בפלטפורמה הוא באחריותך בלבד.
          </Text>
        </View>
      </ScrollView>
    </View>
  );
}

/* ============================ */
/* ========== STYLES ========== */
/* ============================ */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.md,
    paddingTop: Platform.OS === 'ios' ? 50 : theme.spacing.lg,
    backgroundColor: theme.colors.backgroundLight,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  headerTitle: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  placeholder: {
    width: 40,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: theme.spacing.lg,
    paddingBottom: theme.spacing.xl * 2,
  },
  title: {
    fontSize: 28,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    textAlign: 'center',
    marginBottom: theme.spacing.sm,
  },
  updateDate: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    textAlign: 'center',
    marginBottom: theme.spacing.lg,
  },
  warningBox: {
    backgroundColor: '#ffa50020',
    borderWidth: 2,
    borderColor: '#ffa500',
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.xl,
  },
  warningTitle: {
    fontSize: 20,
    fontFamily: theme.fonts.bold,
    color: '#ffa500',
    textAlign: 'center',
    marginBottom: theme.spacing.sm,
  },
  warningText: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.text,
    textAlign: 'center',
    lineHeight: 24,
  },
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    fontSize: 20,
    fontFamily: theme.fonts.bold,
    color: theme.colors.primary,
    marginBottom: theme.spacing.md,
    textAlign: 'right',
  },
  paragraph: {
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    lineHeight: 24,
    textAlign: 'right',
    marginBottom: theme.spacing.sm,
  },
  bold: {
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  listItem: {
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    lineHeight: 24,
    textAlign: 'right',
    marginBottom: theme.spacing.xs,
    paddingRight: theme.spacing.md,
  },
  footer: {
    backgroundColor: theme.colors.secondary,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.xl,
    borderWidth: 2,
    borderColor: theme.colors.primary,
  },
  footerTitle: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: theme.colors.primary,
    textAlign: 'center',
    marginBottom: theme.spacing.sm,
  },
  footerText: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: theme.spacing.sm,
  },
  footerHighlight: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    textAlign: 'center',
    marginTop: theme.spacing.sm,
  },
});
